import type { GamePatch } from './bulkPlan'
import type { GameLike } from './types'

/**
 * What a bulk row's fate is once the dust settles.
 *
 * `unconfirmed` exists because `GamesController::update` saves the game and
 * THEN does roster work, with no transaction around the pair. A 5xx or a
 * gateway timeout on that request tells you nothing about whether the row was
 * written — so rolling back would be as likely to lie as keeping the patch, and
 * auto-retrying could double-apply a shift.
 */
export type MutationOutcome =
  | 'rejected'
  | 'saved'
  | 'not-saved'
  | 'conflict'
  | 'unconfirmed'

export interface ReconciliationInput {
  /** The fields we asked the API to set. */
  intendedPatch: GamePatch
  /** The row as it was before we touched it. */
  preEdit: GameLike
  /** The row as `GET /games/{id}` reports it now, or null if unreachable. */
  observed: GameLike | null
}

const asInstant = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null
  }
  const millis = Date.parse(value)
  return Number.isFinite(millis) ? millis : null
}

const asNumericId = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const asUid = (value: unknown): string | null => {
  if (value == null) {
    return null
  }
  const text = String(value)
  return text.length === 0 ? null : text
}

/** `''` and `null` both mean "no game number" — the column holds both. */
const asOptionalText = (value: unknown): string =>
  value == null ? '' : String(value)

/**
 * Reads the value a patch key corresponds to off a game row.
 *
 * The patch speaks the WRITE contract (`venue_id`) while the row speaks the
 * READ one (`venue.id`); comparing them without this mapping would report every
 * successful save as a conflict.
 */
const readFromGame = (game: GameLike, key: keyof GamePatch): unknown => {
  switch (key) {
    case 'venue_id':
      return game.venue?.id ?? null
    case 'facility_id':
      return game.facility?.id ?? null
    case 'home_team_id':
      return game.homeTeam?.id ?? null
    case 'visiting_team_id':
      return game.visitingTeam?.id ?? null
    //  `schedule` IS the division on a game row — the key the patch uses and
    //  the relation the row carries are named differently.
    case 'schedule_id':
      return game.schedule?.id ?? null
    default:
      return (game as unknown as Record<string, unknown>)[key]
  }
}

/**
 * Field-aware equality.
 *
 * Timestamps compare as INSTANTS, never as strings: the client writes Luxon's
 * `2026-11-15T18:00:00.000Z` and the API reads back PHP's ATOM
 * `2026-11-15T18:00:00+00:00`. Those are the same moment and a string compare
 * would call every single save a conflict.
 */
export const fieldsMatch = (
  key: keyof GamePatch,
  intended: unknown,
  observed: unknown
): boolean => {
  switch (key) {
    case 'starts_at':
    case 'ends_at':
      return asInstant(intended) === asInstant(observed)
    case 'game_type_id':
      return asNumericId(intended) === asNumericId(observed)
    case 'venue_id':
    case 'facility_id':
    case 'home_team_id':
    case 'visiting_team_id':
    case 'schedule_id':
      return asUid(intended) === asUid(observed)
    case 'league_game_number':
      return asOptionalText(intended) === asOptionalText(observed)
    case 'if_necessary_status':
      return Boolean(intended) === Boolean(observed)
    default:
      return intended === observed
  }
}

const patchKeys = (patch: GamePatch): Array<keyof GamePatch> =>
  (Object.keys(patch) as Array<keyof GamePatch>).filter(
    (key) => patch[key] !== undefined
  )

/** One (person, role) pair, as both the patch and the row express it. */
const officialKey = (official: {
  id: string
  official_type_id: number | string
}): string => `${official.id}:${Number(official.official_type_id)}`

/**
 * The game's officials for the roles a patch claims, in the patch's own shape.
 *
 * Scoped, because an officials patch only ever speaks for its own column: a
 * Referees write compared against ALL of a game's officials would report every
 * successful save as a conflict the moment the game also had a scorekeeper.
 */
const scopedOfficials = (
  game: GameLike,
  scope: number[] | null | undefined
): NonNullable<GamePatch['officials']> =>
  (game.officials ?? [])
    .filter(
      (official) =>
        scope == null || scope.includes(Number(official.official_type_id))
    )
    .map((official) => ({
      id: official.id,
      official_type_id: Number(official.official_type_id),
    }))

/**
 * Officials compare as a SET of pairs, never by reference.
 *
 * The generic comparison is `intended === observed`, which for two arrays is
 * always false — so before this existed, EVERY officials edit came back as
 * "changed by someone else" even when it had saved perfectly.
 */
const officialsMatch = (patch: GamePatch, game: GameLike): boolean => {
  const intended = new Set((patch.officials ?? []).map(officialKey))
  const observed = new Set(
    scopedOfficials(game, patch.officials_scope).map(officialKey)
  )
  return (
    intended.size === observed.size &&
    Array.from(intended).every((key) => observed.has(key))
  )
}

/**
 * Compares a patch against a row.
 *
 * Two keys are excluded from the generic comparison:
 *
 * - `if_necessary_status` rides along on every patch purely for backwards
 *   compatibility, so a row that matches on everything else but differs on that
 *   flag is not a conflict.
 * - `officials_scope` is an INSTRUCTION, not a field — it tells the server which
 *   roles to reconcile and has no counterpart on the row at all.
 *
 * `officials` is compared separately, as a set of pairs scoped by that same
 * instruction.
 */
export const patchMatchesGame = (patch: GamePatch, game: GameLike): boolean => {
  const generic = patchKeys(patch)
    .filter(
      (key) =>
        key !== 'if_necessary_status' &&
        key !== 'officials' &&
        key !== 'officials_scope'
    )
    .every((key) => fieldsMatch(key, patch[key], readFromGame(game, key)))

  if (!generic) {
    return false
  }
  return patch.officials === undefined || officialsMatch(patch, game)
}

/**
 * Decides what actually happened to a row whose PUT did not return cleanly.
 *
 * Only ever called after a normalized comparison — classifying on raw values
 * would turn a serialization difference into a conflict and an unchanged row
 * into a phantom save.
 */
export const classifyReconciliation = ({
  intendedPatch,
  preEdit,
  observed,
}: ReconciliationInput): MutationOutcome => {
  if (observed === null) {
    return 'unconfirmed'
  }
  if (patchMatchesGame(intendedPatch, observed)) {
    return 'saved'
  }
  //  Same as it was before we asked: the write never landed, so the row can be
  //  rolled back and offered for retry without risking a double-apply.
  const preEditShape: GamePatch = patchKeys(intendedPatch).reduce(
    (acc, key) => {
      //  Officials do not round-trip through `readFromGame`: the row shape
      //  carries names and every role, while the patch shape is scoped pairs.
      if (key === 'officials') {
        return {
          ...acc,
          officials: scopedOfficials(preEdit, intendedPatch.officials_scope),
        }
      }
      //  An instruction, so it is copied across rather than read off the row.
      if (key === 'officials_scope') {
        return { ...acc, officials_scope: intendedPatch.officials_scope }
      }
      return { ...acc, [key]: readFromGame(preEdit, key) }
    },
    {} as GamePatch
  )
  if (patchMatchesGame(preEditShape, observed)) {
    return 'not-saved'
  }
  //  Neither our value nor the one we started from — somebody else moved it.
  return 'conflict'
}

export interface HttpFailureLike {
  status?: number
  statusCode?: number
  name?: string
}

/** 4xx is the server refusing the input; anything else leaves state unknown. */
export const isRejection = (
  error: HttpFailureLike | null | undefined
): boolean => {
  const status = Number(error?.status ?? error?.statusCode ?? 0)
  return status >= 400 && status < 500
}

/**
 * The single-game response omits the venue's facilities, so a row rehydrated
 * straight from it loses the surface options the grid needs to render and edit.
 * Re-attach them from the full venue map.
 */
export const rehydrateVenueFacilities = <TGame extends GameLike>(
  game: TGame,
  venuesById: Map<string, GameLike['venue']>
): TGame => {
  const venueId = game.venue?.id
  if (venueId == null) {
    return game
  }
  const known = venuesById.get(venueId)
  if (known == null || known.facilities == null) {
    return game
  }
  return {
    ...game,
    venue: { ...game.venue, id: venueId, facilities: known.facilities },
  }
}
